const FLOW_CONCEPTS={
  revenue:['RevenueFromContractWithCustomerExcludingAssessedTax','SalesRevenueNet','Revenues'],
  grossProfit:['GrossProfit'],
  operatingIncome:['OperatingIncomeLoss'],
  netIncome:['NetIncomeLoss','ProfitLoss'],
  operatingCashFlow:['NetCashProvidedByUsedInOperatingActivities'],
  capex:['PaymentsToAcquirePropertyPlantAndEquipment','PaymentsForAdditionsToPropertyPlantAndEquipment']
};
const INSTANT_CONCEPTS={
  cash:['CashAndCashEquivalentsAtCarryingValue','CashCashEquivalentsRestrictedCashAndRestrictedCashEquivalents'],
  debt:['LongTermDebtAndFinanceLeaseObligationsCurrent','LongTermDebtCurrent','LongTermDebtNoncurrent','LongTermDebtAndFinanceLeaseObligationsNoncurrent']
};
const ANNUAL_FORMS=new Set(['10-K','20-F','40-F']);

function unitsFor(payload,concept,namespace='us-gaap'){return payload?.facts?.[namespace]?.[concept]?.units||{};}
function numericRows(payload,concept,unit='USD',namespace='us-gaap'){const units=unitsFor(payload,concept,namespace);const rows=units?.[unit]||[];return rows.filter(r=>Number.isFinite(Number(r?.val))&&r?.end);}
function latestInstantAny(payload,aliases,unit='USD',namespace='us-gaap'){for(const concept of aliases){const rows=numericRows(payload,concept,unit,namespace).sort((a,b)=>String(b.end||'').localeCompare(String(a.end||''))||String(b.filed||'').localeCompare(String(a.filed||'')));if(rows[0])return{concept,value:Number(rows[0].val),end:rows[0].end,filed:rows[0].filed||null,form:rows[0].form||null,namespace};}return null;}
function annualSeries(payload,aliases,limit=4){const byEnd=new Map();for(const concept of aliases){const rows=numericRows(payload,concept).filter(r=>ANNUAL_FORMS.has(r.form)&&(!r.fp||String(r.fp).toUpperCase()==='FY')).sort((a,b)=>String(b.filed||'').localeCompare(String(a.filed||'')));for(const r of rows){if(!byEnd.has(r.end))byEnd.set(r.end,{concept,value:Number(r.val),end:r.end,filed:r.filed||null,form:r.form||null,fy:r.fy??null,fp:r.fp??null});}}return[...byEnd.values()].sort((a,b)=>String(b.end||'').localeCompare(String(a.end||''))||String(b.filed||'').localeCompare(String(a.filed||''))).slice(0,limit);}
function latestAnnual(payload,aliases,anchorEnd=null){if(anchorEnd){const row=annualSeries(payload,aliases,20).find(r=>r.end===anchorEnd);return row||null;}return annualSeries(payload,aliases,1)[0]||null;}
function latestInstantAt(payload,aliases,end){for(const concept of aliases){const rows=numericRows(payload,concept).filter(r=>r.end===end).sort((a,b)=>String(b.filed||'').localeCompare(String(a.filed||'')));if(rows[0])return{concept,value:Number(rows[0].val),end:rows[0].end,filed:rows[0].filed||null,form:rows[0].form||null};}return null;}
function pct(num,den){return Number.isFinite(num)&&Number.isFinite(den)&&den!==0?num/den*100:null;}

export function deriveSecFinancials(payload){
  const revenueSeries=annualSeries(payload,FLOW_CONCEPTS.revenue,3);
  const revenue=revenueSeries[0]||null;
  if(!revenue)return{status:'MISSING',source:'SEC Company Facts',fiscalEnd:null,filedAt:null,metrics:{},evidence:[]};
  const previousRevenue=revenueSeries[1]||null;
  const end=revenue.end;
  const previousEnd=previousRevenue?.end||null;
  const gross=latestAnnual(payload,FLOW_CONCEPTS.grossProfit,end);
  const operating=latestAnnual(payload,FLOW_CONCEPTS.operatingIncome,end);
  const net=latestAnnual(payload,FLOW_CONCEPTS.netIncome,end);
  const ocf=latestAnnual(payload,FLOW_CONCEPTS.operatingCashFlow,end);
  const capex=latestAnnual(payload,FLOW_CONCEPTS.capex,end);
  const cash=latestInstantAt(payload,INSTANT_CONCEPTS.cash,end);
  const debtParts=INSTANT_CONCEPTS.debt.map(c=>latestInstantAt(payload,[c],end)).filter(Boolean);
  const debt=debtParts.length?debtParts.reduce((s,x)=>s+x.value,0):null;
  const shares=latestInstantAny(payload,['EntityCommonStockSharesOutstanding'],'shares','dei');
  const fcf=ocf&&capex?ocf.value-capex.value:null;
  const prevGross=previousEnd?latestAnnual(payload,FLOW_CONCEPTS.grossProfit,previousEnd):null;
  const prevOperating=previousEnd?latestAnnual(payload,FLOW_CONCEPTS.operatingIncome,previousEnd):null;
  const prevNet=previousEnd?latestAnnual(payload,FLOW_CONCEPTS.netIncome,previousEnd):null;
  const prevOcf=previousEnd?latestAnnual(payload,FLOW_CONCEPTS.operatingCashFlow,previousEnd):null;
  const prevCapex=previousEnd?latestAnnual(payload,FLOW_CONCEPTS.capex,previousEnd):null;
  const prevFcf=prevOcf&&prevCapex?prevOcf.value-prevCapex.value:null;
  const previousMetrics=previousRevenue?{
    revenueAnnual:previousRevenue.value,
    grossProfitAnnual:prevGross?.value??null,
    grossMarginPct:prevGross?pct(prevGross.value,previousRevenue.value):null,
    operatingIncomeAnnual:prevOperating?.value??null,
    operatingMarginPct:prevOperating?pct(prevOperating.value,previousRevenue.value):null,
    netIncomeAnnual:prevNet?.value??null,
    operatingCashFlowAnnual:prevOcf?.value??null,
    capexAnnual:prevCapex?.value??null,
    freeCashFlowAnnual:prevFcf
  }:null;
  const metrics={
    revenueAnnual:revenue.value,
    grossProfitAnnual:gross?.value??null,
    grossMarginPct:gross?pct(gross.value,revenue.value):null,
    operatingIncomeAnnual:operating?.value??null,
    operatingMarginPct:operating?pct(operating.value,revenue.value):null,
    netIncomeAnnual:net?.value??null,
    operatingCashFlowAnnual:ocf?.value??null,
    capexAnnual:capex?.value??null,
    freeCashFlowAnnual:fcf,
    cashAndEquivalents:cash?.value??null,
    totalDebt:debt,
    sharesOutstanding:shares?.value??null
  };
  const evidence=[revenue,gross,operating,net,ocf,capex,cash,...debtParts,shares].filter(Boolean).map(x=>({concept:x.concept,end:x.end,filed:x.filed,form:x.form,value:x.value}));
  const populated=Object.values(metrics).filter(v=>v!==null&&v!==undefined).length;
  return{status:populated>=4?'VERIFIED':'PARTIAL',source:'SEC Company Facts',fiscalEnd:end,previousFiscalEnd:previousEnd,filedAt:revenue.filed||null,sharesOutstandingAsOf:shares?.end??null,metrics,previousMetrics,evidence};
}
